<template>
  <div class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11">
      <p>9. März 2025 von Theo Döllmann</p>
      <h1>Verspätungsverteilungen zeigen</h1>
      <p>
        Seit Dezember 2024 werden zu den Ankunftszeiten auch Wahrscheinlichkeitsverteilungen
        angezeigt. Hier folgt nun eine kleine Einführung, wie diese zu verstehen sind und was man
        dort ablesen kann.
      </p>
      <p>
        <b>TLDR:</b> Die Histogramme zeigen die von uns vorhergesagte Abweichung von der bekannten
        Zeit. Das kann entweder die Fahrplan-Zeit sein, oder eine von der Deutschen Bahn erstellte
        Zeitprognose.
      </p>
      <h2>Kurzübersicht</h2>
      <div class="d-flex flex-wrap justify-content-center hyphenate gap-5">
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(10)"
              :prediction="emptyPrediction"
            ></TimeHistogram>
            <figcaption class="figure-caption">Abweichung von Fahrplan</figcaption>
          </figure>
        </div>
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(16)"
              :planned-time="dayjs().hour(13).minute(10)"
              :prediction="emptyPrediction"
            ></TimeHistogram>
            <figcaption class="figure-caption">Abweichung von Echtzeitprognose</figcaption>
          </figure>
        </div>
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(10)"
              :prediction="verySharpPrediction"
            ></TimeHistogram>
            <figcaption class="figure-caption">
              Extrem spitz: Zeit stimmt ziemlich sicher
            </figcaption>
          </figure>
        </div>
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(10)"
              :prediction="realisticPredictionFirstStop"
            ></TimeHistogram>
            <figcaption class="figure-caption">Spitz: Zeit stimmt vermutlich</figcaption>
          </figure>
        </div>
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(10)"
              :prediction="realisticPredictionLastStop"
            ></TimeHistogram>
            <figcaption class="figure-caption">Stumpf: Zeit könnte durchaus falsch sein</figcaption>
          </figure>
        </div>
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(13).minute(10)"
              :prediction="realtimePrediction"
            ></TimeHistogram>
            <figcaption class="figure-caption">Flach: Zeit ist nur eine Schätzung</figcaption>
          </figure>
        </div>
      </div>
      <p>
        Die Histogramme sind in Y-Richtung bei 50 % und in X-Richtung bei -2 und +10 Minuten
        abgeschnitten. In der Regel sind in diesem Bereich alle relevanten Werte abgebildet.
      </p>

      <h2>Zeitangaben sind immer unsicher</h2>
      <p>
        Egal wie gut ein Fahrplan oder eine Echtzeitprognose der Deutschen Bahn ist, ganz sicher
        kann sie nie sein. Es gibt immer einen Grund, warum ein Zug doch noch später oder früher
        ankommt als angegeben. Apps wie z.B. der DB-Navigator, Öffi und Co, zeigen immer nur eine
        konkrete Zeit an. Das ist einfach zu verstehen, aber häufig führt die Ungenauigkeit
        zumindest bei mir auch zu Unzufriedenheit. Ich habe keine Möglichkeit zu wissen, mit welcher
        Sicherheit der Zug zur angegebenen Zeit tatsächlich erscheint. Ich stellen mir dann häufig
        folgende Frage: Kann ich mir vielleicht noch etwas mehr Zeit lassen, weil der Zug vermutlich
        eh noch ein paar Minuten Verspätung sammelt, oder sollte ich sogar schon etwas früher am
        Bahnhof sein, weil der Zug Verspätung aufholen könnte? Zumindest letzteres wäre sehr
        ärgerlich. Das weiß die Deutsche Bahn und macht daher sehr konservative
        Verspätungsprognosen. Ist eine Prognose um zwei Minuten zu hoch (der Zug fährt früher als
        angegeben), verpassen potentiell Fahrgäste den Zug. Ist die Prognose um fünf Minuten zu
        niedrig, haben sich vielleicht einige Fahrgäste zu sehr beeilt um den Zug noch zu kriegen
        und müssen fünf Minuten länger warten. Auch Ärgerlich, aber nicht so schlimm wie ein
        verpasster Zug.
      </p>
      <h2>Unsicherheit sichtbar machen</h2>
      <p>
        Bei Bahn-Vorhersage beschäftigen wir uns schon lange mit diesen Unsicherheiten, denn am Ende
        des Tages beruht genau darauf die Vorhersage der Anschlusssicherheit: Unser KI-Modell
        bestimmt für jeden Ankunfts- und Abfahrtszeit die Unsicherheit. Konkret bedeutet das, dass
        wir für jeden Halt eine Wahrscheinlichkeit vorhersagen können, zu der der Zug 1, 2, oder 3
        Minuten früher als angegeben fährt. Ebenso können wir die Wahrscheinlichkeit vorhersagen,
        dass die Zeitangabe der Bahn genau richtig ist, oder das er 1, 2, 4, 4, ... oder 30 Minuten
        mehr Verspätet sein wird. Dabei ist es Statistisch gesehen übrigens ähnlich Wahrscheinlich,
        dass der Zug 3 Minuten zu früh oder 30 Minuten zu spät kommt. Diese Wahrscheinlichkeiten
        lassen sich in einem Histogramm darstellen.
      </p>
      <p>
        Dafür betrachten wir im Folgenden den MEX 18 auf der Fahrt von Tübingen nach Stuttgart. Bei
        der Abfahrt in Tübingen Hbf hat unser KI-Modell folgende Wahrscheinlichkeitswerte
        vorhergesagt. Vorhergesagt ist jeweils die Wahrscheinlichkeit, dass die tatsächliche
        Abfahrtszeit des Zuges von der geplanten Abfahrtszeit um eine gewisse Anzahl an Minuten
        abweicht.
      </p>
      <div class="table-responsive" style="white-space: nowrap">
        <table class="table table-bordered">
          <caption>
            Wahrscheinlichkeit der Abweichung von planmäßiger Abfahrt (MEX 18)
          </caption>
          <tbody>
            <tr>
              <th>Abfahrt</th>
              <td>≤ -1</td>
              <td>+ 0</td>
              <td>+ 1</td>
              <td>+ 2</td>
              <td>+ 3</td>
              <td>+ 4</td>
              <td>+ 5</td>
              <td>≥ +6</td>
            </tr>
            <tr>
              <th>Vorhersage</th>
              <td>1 %</td>
              <td>44 %</td>
              <td>21 %</td>
              <td>10 %</td>
              <td>6 %</td>
              <td>4 %</td>
              <td>2 %</td>
              <td>12 %</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Aus diesen Daten ergibt sich ein relativ spitzes Histogramm. Nach rechts, also in Richtung
        von hohen Verspätungen, nimmt die Wahrscheinlichkeit schnell ab.
      </p>
      <div class="d-flex justify-content-center">
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(22).minute(33)"
              :prediction="realisticPredictionFirstStop"
            ></TimeHistogram>
            <figcaption class="figure-caption">MEX 18 Tübingen Hbf (Abfahrt)</figcaption>
          </figure>
        </div>
      </div>
      <p>
        Es ist unwahrscheinlich, dass der Zug genau 5 oder genau 6 Minuten Verspätung haben wird.
        Höhere Verspätungen sind noch unwahrscheinlicher. Dass aber irgendeine der höheren
        Verspätungen eintritt, ist dennoch nicht unwahrscheinlich: Mit einer Wahrscheinlichkeit von
        12 % soll der Zug mindestens 6 Minuten zu spät abfahren. Dieses Muster, was man im
        Englischen als den "long tail" des Histogramms bezeichnet, ist typisch für viele Züge.
      </p>

      <p>
        Auch typisch ist, dass sich die Wahrscheinlichkeitsverteilungen im Verlauf der Fahrt stark
        ändern. Denn typischerweise fahren Züge eher pünktlich los und sammeln dann tendenziell auf
        der Strecke mehr und mehr Verspätungen. Damit sinkt meistens die Wahrscheinlichkeit, dass
        ein Zug genau pünktlich ist, und es steigt die Wahrscheinlichkeit, dass er Verspätung hat.
        Betrachten wir den MEX 18 von gerade eben weiter, so sehen wir für seinen Halt in Stuttgart
        Hbf (ca. 1 h später) folgende Wahrscheinlichkeitsverteilung:
      </p>

      <div class="d-flex justify-content-center">
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(23).minute(32)"
              :prediction="realisticPredictionLastStop"
            ></TimeHistogram>
            <figcaption class="figure-caption">
              MEX 18 Stuttgart Hbf <br />
              (Ankunft)
            </figcaption>
          </figure>
        </div>
      </div>

      <p>
        Das Histogramm wirkt schon auf den ersten Blick deutlich flacher und breiter. Schaut man
        genauer hin, gibt es nun auch eine höhere Chance, dass der Zug zu früh ankommt. Laut der
        Vorhersage des KI-Modells ist dies nun 5 % wahrscheinlich. Dafür kommt der Zug nur noch mit
        ca. 25 % Sicherheit genau pünktlich an. Keine Sorge, Züge fahren in der Regel fast nie zu
        früh ab, eine verfrühte Ankunft hingegen kommt ab und zu durchaus vor.
      </p>

      <p>
        Am unberechenbarsten sind Züge, denen die Bahn schon eine Verspätung prognostiziert, wie
        folgendes Histogramm des gleichen Zuges an einem anderen Tag zeigt:
      </p>
      <div class="d-flex justify-content-center">
        <div>
          <figure class="figure" style="display: block; width: 150px">
            <TimeHistogram
              class="figure-img"
              width="150px"
              height=""
              :time="dayjs().hour(23).minute(39)"
              :planned-time="dayjs().hour(23).minute(32)"
              :prediction="realtimePrediction"
            ></TimeHistogram>
            <figcaption class="figure-caption">
              MEX 18 Stuttgart Hbf <br />
              mit Verspätung (Ankunft)
            </figcaption>
          </figure>
        </div>
      </div>
      <p>
        Das Histogramm ist so Flach, dass kaum abschätzbar ist, wann der Zug tatsächlich ankommen
        soll. Wichtig ist hier zu beachten, dass das Histogramm die Vorhergesagte Abweichung von der
        DB-Verspätungsprognose zeigt, also von 23:39 Uhr aus, nicht von der geplanten Ankunft um
        23:32 Uhr.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'default' })

useHead({
  title: 'Blog: Verspätungshistogramme',
  meta: [
    {
      name: 'description',
      content: 'Blog: Wie Bahn-Vorhersage die Unsicherheit von Verspätungen darstellt.'
    }
  ]
})

import TimeHistogram from '~/components/JourneyDisplay/TimeHistogram.vue'
import dayjs from 'dayjs'
import { type DelayPrediction } from '~/types/fptfTypes'

const deterministcPrediction: DelayPrediction = {
  type: 'delayPrediction',
  offset: 3,
  predictions: [0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
}

const emptyPrediction: DelayPrediction = {
  type: 'delayPrediction',
  offset: 3,
  predictions: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
}

const verySharpPrediction: DelayPrediction = {
  predictions: [
    0.00039, 0.00052, 0.00378, 0.64932, 0.1899, 0.03836, 0.03225, 0.02329, 0.01424, 0.01032,
    0.00745, 0.00429, 0.00383, 0.00306, 0.00268, 0.00294, 0.00162, 0.00109, 0.00154, 0.00096,
    0.00088, 0.00085, 0.00122, 0.00083, 0.00081, 0.00051, 0.00074, 0.00041, 0.00044, 0.00041,
    0.00031, 0.00023, 0.00028, 0.00025
  ],
  offset: 3,
  type: 'delayPrediction'
}

const realisticPredictionFirstStop: DelayPrediction = {
  predictions: [
    0.00097, 0.00166, 0.01065, 0.43869, 0.21216, 0.09996, 0.06415, 0.03922, 0.02298, 0.01576,
    0.00965, 0.01002, 0.00811, 0.00609, 0.00614, 0.00506, 0.00509, 0.0031, 0.00454, 0.00419,
    0.00456, 0.00327, 0.0024, 0.00254, 0.00294, 0.00192, 0.00122, 0.00088, 0.00146, 0.00162,
    0.00346, 0.00148, 0.00208, 0.00197
  ],
  offset: 3,
  type: 'delayPrediction'
}

const realisticPredictionLastStop: DelayPrediction = {
  predictions: [
    0.00216, 0.00367, 0.03922, 0.24836, 0.22269, 0.14348, 0.08625, 0.04803, 0.03018, 0.0246, 0.0172,
    0.01503, 0.01129, 0.01226, 0.01097, 0.00676, 0.0104, 0.007, 0.00617, 0.00391, 0.00506, 0.00537,
    0.00512, 0.00406, 0.00422, 0.00377, 0.00382, 0.002, 0.00272, 0.0025, 0.00282, 0.003, 0.00312,
    0.00281
  ],
  offset: 3,
  type: 'delayPrediction'
}

const realtimePrediction: DelayPrediction = {
  predictions: [
    0.06405, 0.02938, 0.07309, 0.09564, 0.07521, 0.06499, 0.0732, 0.07514, 0.03605, 0.03367, 0.0236,
    0.03134, 0.02248, 0.02497, 0.04221, 0.02401, 0.01376, 0.02479, 0.01698, 0.01367, 0.01548,
    0.01862, 0.0161, 0.01162, 0.02457, 0.008, 0.00849, 0.00507, 0.00666, 0.00422, 0.00431, 0.00543,
    0.00629, 0.00691
  ],
  offset: 3,
  type: 'delayPrediction'
}
</script>
